/**
 * Request Detail View Component
 *
 * Displays the full details of an expenditure request including:
 * - Request header with title and status badge
 * - Approval progress stepper (visual timeline)
 * - Request information card
 * - Attachments section with download links
 * - Audit trail (collapsible)
 * - Action buttons based on user role and status
 */

import { renderIcon } from '../utils/icons';
import { createLink } from '../components/link';
import { createCommentsSection, type Comment } from '../components/comments-section';
import { createInlineApprovalActions } from '../components/inline-approval-actions';
import { confirm } from '../components/modal';
import { apiClient } from '../services/api-client';
import {
  subscribeToAuth,
  selectUser,
  type AuthState,
} from '../services/auth-store';
import { createSidebarLayout } from '../components/sidebar-layout';
import type { RouteContext } from '../services/router';
import { getExpenseReportsForRequest } from '../services/expense-reports-api';
import { createExpenseReportsList } from '../components/expense-reports-list';
import { createBudgetProgress } from '../components/budget-progress';
import { showExpenseReportDetailModal } from '../components/expense-report-detail-modal';
import { getExpenseReportDetails } from '../services/expense-reports-api';

/**
 * Expenditure category type
 */
type ExpenditureCategory =
  | 'TRAVEL'
  | 'EQUIPMENT'
  | 'SOFTWARE'
  | 'TRAINING'
  | 'MARKETING'
  | 'OFFICE_SUPPLIES'
  | 'PROFESSIONAL_SERVICES'
  | 'OTHER';

/**
 * Expenditure frequency type
 */
type ExpenditureFrequency = 'ONE_TIME' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';

/**
 * Expenditure request data from API
 */
interface ExpenditureRequest {
  id: string;
  title: string | null;
  description: string | null;
  amount: number | null;
  currency: string | null;
  category: ExpenditureCategory | null;
  frequency: ExpenditureFrequency | null;
  status: RequestStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  completed_at: string | null;
  // Creator info from user_cache join (may be null if user not in cache)
  creator_name: string | null;
  creator_email: string | null;
}

/**
 * Request status type
 */
type RequestStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

/**
 * Approval step status type
 */
type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'DELEGATED';

/**
 * Approver role type
 */
type ApproverRole = 'DEPARTMENT_HEAD' | 'CFO' | 'CEO';

/**
 * Approval step from API (camelCase to match API response)
 */
interface ApprovalStep {
  id: string;
  requestId: string;
  stepOrder: number;
  approverRole: ApproverRole;
  approverId: string | null;
  approverName: string | null;
  approverEmail: string | null;
  delegateId: string | null;
  actedBy: string | null;
  actedByName: string | null;
  actedByEmail: string | null;
  status: ApprovalStatus;
  decisionAt: string | null;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
  isCurrentStep: boolean;
  isActedByDeputy: boolean;
  onBehalfOfId: string | null;
}

/**
 * Approval progress data
 */
interface ApprovalProgress {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  currentStepOrder: number | null;
}

/**
 * Attachment data from API
 */
interface Attachment {
  id: string;
  request_id: string;
  original_filename: string;
  stored_filename: string;
  mime_type: string;
  file_size: number;
  uploaded_by: string;
  created_at: string;
}

/**
 * Audit log entry from API
 */
interface AuditLogEntry {
  id: string;
  request_id: string | null;
  user_id: string;
  action: string;
  details: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

/**
 * Status badge colors
 */
const statusStyles: Record<RequestStatus, { bg: string; text: string; icon: string }> = {
  DRAFT: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'edit' },
  PENDING: { bg: 'bg-warning/20', text: 'text-warning', icon: 'clock' },
  APPROVED: { bg: 'bg-success/20', text: 'text-success', icon: 'checkCircle' },
  REJECTED: { bg: 'bg-destructive/20', text: 'text-destructive', icon: 'xCircle' },
  CANCELLED: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'x' },
};

/**
 * Approval step status styles
 */
const approvalStatusStyles: Record<ApprovalStatus, { bg: string; text: string; border: string }> = {
  PENDING: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-muted-foreground/30' },
  APPROVED: { bg: 'bg-success/20', text: 'text-success', border: 'border-success' },
  REJECTED: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive' },
  DELEGATED: { bg: 'bg-info/20', text: 'text-info', border: 'border-info' },
};

/**
 * Approver role labels
 */
const roleLabels: Record<ApproverRole, string> = {
  DEPARTMENT_HEAD: 'Department Head',
  CFO: 'CFO',
  CEO: 'CEO',
};

/**
 * Expense type labels - matching the frontend form constants
 */
const expenseTypeLabels: Record<ExpenditureCategory, string> = {
  OFFICE_SUPPLIES: 'Office Material',
  TRAINING: 'External Event/Conference',
  OTHER: 'Internal Company Event/Workshop',
  TRAVEL: 'Travel',
  MARKETING: 'Marketing',
  EQUIPMENT: 'Out-of-Pocket Expense',
  PROFESSIONAL_SERVICES: 'Other',
  SOFTWARE: 'Software',
};

/**
 * Frequency labels - matching the frontend form constants
 */
const frequencyLabels: Record<ExpenditureFrequency, string> = {
  ONE_TIME: 'One Time',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUAL: 'Annual',
};

/**
 * Format currency amount
 */
function formatAmount(amount: number | null, currency: string | null): string {
  if (amount === null || currency === null) {
    return '-';
  }
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date time
 */
function formatDateTime(timestamp: string | null): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-CH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CH', { month: 'short', day: 'numeric' });
}

/**
 * Format file size
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Get file icon based on MIME type
 */
function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'fileText';
  return 'file';
}

/**
 * Create status badge element
 */
function createStatusBadge(status: RequestStatus): HTMLElement {
  const style = statusStyles[status];
  const badge = document.createElement('span');
  badge.className = `inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${style.bg} ${style.text}`;
  badge.innerHTML = `
    ${renderIcon(style.icon as Parameters<typeof renderIcon>[0], { size: 'xs' }).outerHTML}
    <span>${status.charAt(0) + status.slice(1).toLowerCase()}</span>
  `;
  return badge;
}

/**
 * Get display name for an approver
 * Returns the approver name if available, otherwise falls back to role label
 */
function getApproverDisplayName(step: ApprovalStep): string {
  // If there's an actedBy (someone who took action), show them for completed steps
  if ((step.status === 'APPROVED' || step.status === 'REJECTED') && step.actedByName) {
    return step.actedByName;
  }
  // Otherwise show the assigned approver name
  if (step.approverName) {
    return step.approverName;
  }
  // Fall back to role label if no name available
  return roleLabels[step.approverRole];
}

/**
 * Create approval stepper element
 */
function createApprovalStepper(steps: ApprovalStep[], progress: ApprovalProgress | null): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('div');
  header.className = 'flex items-center gap-3 mb-6';

  // Show progress summary in header if available
  let progressSummary = '';
  if (progress && progress.total > 0) {
    progressSummary = `<span class="ml-auto text-sm text-muted-foreground">${progress.approved}/${progress.total} approved</span>`;
  }

  header.innerHTML = `
    ${renderIcon('checkCircle', { size: 'md', className: 'text-primary' }).outerHTML}
    <h2 class="text-lg font-semibold text-foreground">Approval Progress</h2>
    ${progressSummary}
  `;
  container.appendChild(header);

  if (!steps || steps.length === 0) {
    const emptyState = document.createElement('div');
    emptyState.className = 'text-center py-4 text-muted-foreground';
    emptyState.textContent = 'No approval steps yet. Submit the request to start the approval process.';
    container.appendChild(emptyState);
    return container;
  }

  const stepperList = document.createElement('div');
  stepperList.className = 'relative';

  steps.forEach((step, index) => {
    const isLast = index === steps.length - 1;
    const style = approvalStatusStyles[step.status] || approvalStatusStyles.PENDING;

    const stepItem = document.createElement('div');
    stepItem.className = 'flex gap-4 pb-6 last:pb-0';

    // Connector line and step circle
    const stepIndicator = document.createElement('div');
    stepIndicator.className = 'flex flex-col items-center';
    stepIndicator.innerHTML = `
      <div class="w-10 h-10 rounded-full ${style.bg} ${style.border} border-2 flex items-center justify-center shrink-0 relative z-10">
        ${step.status === 'APPROVED'
          ? renderIcon('check', { size: 'sm', className: style.text }).outerHTML
          : step.status === 'REJECTED'
          ? renderIcon('x', { size: 'sm', className: style.text }).outerHTML
          : step.isCurrentStep
          ? renderIcon('clock', { size: 'sm', className: 'text-primary' }).outerHTML
          : `<span class="text-sm font-medium ${style.text}">${step.stepOrder}</span>`}
      </div>
      ${!isLast ? `<div class="w-0.5 flex-1 ${step.status === 'APPROVED' ? 'bg-success' : 'bg-border'} mt-2"></div>` : ''}
    `;

    // Step content
    const stepContent = document.createElement('div');
    stepContent.className = 'flex-1 min-w-0 pt-1';

    // Get the display name for the approver
    const approverDisplayName = getApproverDisplayName(step);

    const roleTitle = document.createElement('div');
    roleTitle.className = 'flex items-center justify-between gap-2 mb-1';
    roleTitle.innerHTML = `
      <div class="flex flex-col">
        <span class="font-medium text-foreground">${escapeHtml(approverDisplayName)}</span>
        <span class="text-xs text-muted-foreground">${roleLabels[step.approverRole]}</span>
      </div>
      <span class="text-xs px-2 py-0.5 rounded-full ${style.bg} ${style.text}">
        ${step.status.charAt(0) + step.status.slice(1).toLowerCase()}
      </span>
    `;
    stepContent.appendChild(roleTitle);

    // Show "on behalf of" info when someone acted as deputy
    if (step.isActedByDeputy && step.approverName && step.actedByName) {
      const deputyInfo = document.createElement('p');
      deputyInfo.className = 'text-xs text-muted-foreground';
      deputyInfo.textContent = `On behalf of ${step.approverName}`;
      stepContent.appendChild(deputyInfo);
    }

    if (step.decisionAt) {
      const decisionTime = document.createElement('p');
      decisionTime.className = 'text-sm text-muted-foreground';
      decisionTime.textContent = formatDateTime(step.decisionAt);
      stepContent.appendChild(decisionTime);
    }

    if (step.comment) {
      const comment = document.createElement('p');
      comment.className = 'text-sm text-muted-foreground mt-2 italic';
      comment.textContent = `"${step.comment}"`;
      stepContent.appendChild(comment);
    }

    stepItem.appendChild(stepIndicator);
    stepItem.appendChild(stepContent);
    stepperList.appendChild(stepItem);
  });

  container.appendChild(stepperList);
  return container;
}

/**
 * Create request info card
 */
function createRequestInfoCard(request: ExpenditureRequest, creatorName: string | null): HTMLElement {
  const card = document.createElement('div');
  card.className = 'glass-card p-6 rounded-2xl';

  // Format expense type and frequency labels
  const expenseTypeLabel = request.category ? expenseTypeLabels[request.category] : '-';
  const frequencyLabel = request.frequency ? frequencyLabels[request.frequency] : '-';

  card.innerHTML = `
    <div class="flex items-center gap-3 mb-6">
      ${renderIcon('fileText', { size: 'md', className: 'text-primary' }).outerHTML}
      <h2 class="text-lg font-semibold text-foreground">Request Details</h2>
    </div>
    <dl class="space-y-4">
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Title</dt>
        <dd class="text-foreground font-medium text-right max-w-[60%]">${escapeHtml(request.title || '-')}</dd>
      </div>
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Amount</dt>
        <dd class="text-foreground font-medium">${formatAmount(request.amount, request.currency)}</dd>
      </div>
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Expense Type</dt>
        <dd class="text-foreground">${escapeHtml(expenseTypeLabel)}</dd>
      </div>
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Frequency</dt>
        <dd class="text-foreground">${escapeHtml(frequencyLabel)}</dd>
      </div>
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Requested by</dt>
        <dd class="text-foreground">${escapeHtml(creatorName || 'Unknown')}</dd>
      </div>
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Created</dt>
        <dd class="text-foreground">${formatDateTime(request.created_at)}</dd>
      </div>
      ${request.submitted_at ? `
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Submitted</dt>
        <dd class="text-foreground">${formatDateTime(request.submitted_at)}</dd>
      </div>
      ` : ''}
      ${request.completed_at ? `
      <div class="flex justify-between border-b border-border/50 pb-3">
        <dt class="text-muted-foreground">Completed</dt>
        <dd class="text-foreground">${formatDateTime(request.completed_at)}</dd>
      </div>
      ` : ''}
      <div class="pt-2">
        <dt class="text-muted-foreground mb-2">Description</dt>
        <dd class="text-foreground bg-muted/30 p-3 rounded-lg text-sm whitespace-pre-wrap">${escapeHtml(request.description || 'No description provided.')}</dd>
      </div>
    </dl>
  `;

  return card;
}

/**
 * Create attachments card
 */
function createAttachmentsCard(attachments: Attachment[]): HTMLElement {
  const card = document.createElement('div');
  card.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('div');
  header.className = 'flex items-center gap-3 mb-4';
  header.innerHTML = `
    ${renderIcon('paperclip', { size: 'md', className: 'text-primary' }).outerHTML}
    <h2 class="text-lg font-semibold text-foreground">Attachments</h2>
    <span class="ml-auto text-sm text-muted-foreground">${attachments.length} file${attachments.length !== 1 ? 's' : ''}</span>
  `;
  card.appendChild(header);

  if (attachments.length === 0) {
    const emptyState = document.createElement('div');
    emptyState.className = 'text-center py-4 text-muted-foreground';
    emptyState.textContent = 'No attachments uploaded.';
    card.appendChild(emptyState);
    return card;
  }

  const list = document.createElement('div');
  list.className = 'space-y-2';

  attachments.forEach((attachment) => {
    const item = document.createElement('a');
    item.href = `/api/attachments/${attachment.id}/download`;
    item.className = 'flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group';
    item.target = '_blank';
    item.rel = 'noopener noreferrer';

    item.innerHTML = `
      <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        ${renderIcon(getFileIcon(attachment.mime_type) as Parameters<typeof renderIcon>[0], { size: 'md', className: 'text-primary' }).outerHTML}
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
          ${escapeHtml(attachment.original_filename)}
        </p>
        <p class="text-xs text-muted-foreground">
          ${formatFileSize(attachment.file_size)} - ${formatRelativeTime(attachment.created_at)}
        </p>
      </div>
      <div class="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        ${renderIcon('download', { size: 'sm', className: 'text-primary' }).outerHTML}
      </div>
    `;

    list.appendChild(item);
  });

  card.appendChild(list);
  return card;
}

/**
 * Create expense reports card (for Department Head view)
 */
async function createExpenseReportsCard(
  request: ExpenditureRequest,
  _currentUserId: string | null
): Promise<HTMLElement> {
  const card = document.createElement('div');
  card.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('div');
  header.className = 'flex items-center gap-3 mb-4';
  header.innerHTML = `
    ${renderIcon('receipt', { size: 'md', className: 'text-primary' }).outerHTML}
    <h2 class="text-lg font-semibold text-foreground">Expense Reports</h2>
  `;
  card.appendChild(header);

  // Loading state
  const loadingDiv = document.createElement('div');
  loadingDiv.className = 'text-center py-4 text-muted-foreground';
  loadingDiv.innerHTML = `
    ${renderIcon('loader', { size: 'md', className: 'animate-spin mx-auto mb-2' }).outerHTML}
    <p>Loading expense reports...</p>
  `;
  card.appendChild(loadingDiv);

  try {
    // Fetch expense reports for this request
    const reports = await getExpenseReportsForRequest(request.id);

    // Remove loading state
    card.removeChild(loadingDiv);

    if (reports.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.className = 'text-center py-4 text-muted-foreground';
      emptyState.textContent = 'No expense reports submitted yet';
      card.appendChild(emptyState);
      return card;
    }

    // Calculate budget utilization
    const totalSpent = reports.reduce((sum, r) => sum + (r.total_amount_chf || 0), 0);
    const approvedAmount = request.amount || 0;

    // Budget progress bar
    const budgetSection = document.createElement('div');
    budgetSection.className = 'mb-4';
    const budgetProgress = createBudgetProgress({
      approvedAmount,
      spentAmount: totalSpent,
      currency: request.currency || 'CHF',
    });
    budgetSection.appendChild(budgetProgress);
    card.appendChild(budgetSection);

    // Expense reports list
    const reportsList = createExpenseReportsList(reports, async (report) => {
      // Fetch full details and show modal
      try {
        const details = await getExpenseReportDetails(report.id);
        showExpenseReportDetailModal(details, {
          readOnly: true,
          onClose: () => {
            // Modal closed
          },
        });
      } catch (error) {
        console.error('Failed to load expense report details:', error);
        await confirm({
          title: 'Error',
          message: 'Failed to load expense report details. Please try again.',
          confirmText: 'OK',
          icon: 'alertCircle',
        });
      }
    });

    card.appendChild(reportsList);
  } catch (error) {
    console.error('Failed to load expense reports:', error);
    card.removeChild(loadingDiv);

    const errorState = document.createElement('div');
    errorState.className = 'text-center py-4 text-destructive';
    errorState.textContent = 'Failed to load expense reports';
    card.appendChild(errorState);
  }

  return card;
}

/**
 * Create audit trail card (collapsible)
 */
function createAuditTrailCard(auditLog: AuditLogEntry[]): HTMLElement {
  const card = document.createElement('div');
  card.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('button');
  header.type = 'button';
  header.className = 'flex items-center gap-3 w-full text-left';
  header.innerHTML = `
    ${renderIcon('clock', { size: 'md', className: 'text-primary' }).outerHTML}
    <h2 class="text-lg font-semibold text-foreground flex-1">Audit Trail</h2>
    <span class="text-sm text-muted-foreground mr-2">${auditLog.length} entries</span>
    <span class="audit-chevron transition-transform">
      ${renderIcon('chevronDown', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
    </span>
  `;

  const content = document.createElement('div');
  content.className = 'audit-content hidden mt-4';

  if (auditLog.length === 0) {
    content.innerHTML = `
      <div class="text-center py-4 text-muted-foreground">
        No audit entries yet.
      </div>
    `;
  } else {
    const list = document.createElement('div');
    list.className = 'space-y-3';

    // Sort audit log by created_at in reverse chronological order
    const sortedLog = [...auditLog].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    sortedLog.forEach((entry) => {
      const item = document.createElement('div');
      item.className = 'flex gap-3 text-sm';

      const actionIconMap: Record<string, { icon: string; color: string }> = {
        REQUEST_CREATED: { icon: 'plus', color: 'primary' },
        REQUEST_UPDATED: { icon: 'edit', color: 'info' },
        REQUEST_SUBMITTED: { icon: 'send', color: 'primary' },
        REQUEST_CANCELLED: { icon: 'xCircle', color: 'destructive' },
        APPROVAL_GRANTED: { icon: 'checkCircle', color: 'success' },
        APPROVAL_REJECTED: { icon: 'xCircle', color: 'destructive' },
        COMMENT_ADDED: { icon: 'fileText', color: 'muted-foreground' },
        ATTACHMENT_ADDED: { icon: 'paperclip', color: 'muted-foreground' },
        ATTACHMENT_REMOVED: { icon: 'trash', color: 'muted-foreground' },
      };

      const { icon, color } = actionIconMap[entry.action] || { icon: 'info', color: 'muted-foreground' };

      let details = {};
      try {
        details = JSON.parse(entry.details);
      } catch {
        // Ignore parse errors
      }

      item.innerHTML = `
        <div class="w-6 h-6 rounded-full bg-${color}/20 flex items-center justify-center shrink-0 mt-0.5">
          ${renderIcon(icon as Parameters<typeof renderIcon>[0], { size: 'xs', className: `text-${color}` }).outerHTML}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-foreground">${formatAuditAction(entry.action, details)}</p>
          <p class="text-xs text-muted-foreground">${formatDateTime(entry.created_at)}</p>
        </div>
      `;

      list.appendChild(item);
    });

    content.appendChild(list);
  }

  // Toggle functionality
  let isExpanded = false;
  header.addEventListener('click', () => {
    isExpanded = !isExpanded;
    content.classList.toggle('hidden', !isExpanded);
    const chevron = header.querySelector('.audit-chevron');
    if (chevron) {
      chevron.classList.toggle('rotate-180', isExpanded);
    }
  });

  card.appendChild(header);
  card.appendChild(content);
  return card;
}

/**
 * Format audit action for display
 */
function formatAuditAction(action: string, details: Record<string, unknown>): string {
  switch (action) {
    case 'REQUEST_CREATED':
      return 'Request created';
    case 'REQUEST_UPDATED':
      return 'Request updated';
    case 'REQUEST_SUBMITTED':
      return 'Request submitted for approval';
    case 'REQUEST_CANCELLED':
      return 'Request cancelled';
    case 'APPROVAL_GRANTED':
      return `Approved by ${details.approverRole || 'approver'}`;
    case 'APPROVAL_REJECTED':
      return `Rejected by ${details.approverRole || 'approver'}`;
    case 'COMMENT_ADDED':
      return 'Comment added';
    case 'ATTACHMENT_ADDED':
      return `Attachment uploaded: ${details.filename || 'file'}`;
    case 'ATTACHMENT_REMOVED':
      return `Attachment removed: ${details.filename || 'file'}`;
    default:
      return action.replace(/_/g, ' ').toLowerCase();
  }
}

/**
 * Check if the current user can approve the request
 * Returns the current step if they can approve, null otherwise
 */
function canUserApprove(
  currentUserId: string | null,
  approvalSteps: ApprovalStep[]
): ApprovalStep | null {
  if (!currentUserId || !approvalSteps || approvalSteps.length === 0) {
    return null;
  }

  // Find the current pending step that the user can approve
  for (const step of approvalSteps) {
    if (step.status === 'PENDING' && step.isCurrentStep) {
      // User can approve if they are the approver or the delegate
      if (step.approverId === currentUserId || step.delegateId === currentUserId) {
        return step;
      }
    }
  }

  return null;
}

/**
 * Create action buttons based on user role and request status
 */
async function createActionButtons(
  request: ExpenditureRequest,
  currentUserId: string | null,
  userRole: string | null,
  approvalSteps: ApprovalStep[] = []
): Promise<HTMLElement> {
  const container = document.createElement('div');
  container.className = 'flex flex-wrap gap-3';

  const isOwner = request.created_by === currentUserId;
  const isAdmin = userRole === 'ADMIN';

  // Check if user can approve this request
  const canApproveStep = canUserApprove(currentUserId, approvalSteps);

  // Approval actions (approve/reject for pending approver)
  if (request.status === 'PENDING' && canApproveStep) {
    const approvalActions = createInlineApprovalActions({
      request: {
        id: request.id,
        title: request.title,
        amount: request.amount,
        currency: request.currency,
      },
      onActionComplete: () => {
        // Reload the page to show updated status
        window.location.reload();
      },
    });
    // Style the approval buttons more prominently for the detail view
    approvalActions.className = 'flex items-center gap-2';
    const buttons = approvalActions.querySelectorAll('button');
    buttons.forEach((btn) => {
      btn.classList.remove('px-2.5', 'py-1.5', 'text-xs');
      btn.classList.add('px-4', 'py-2', 'text-sm');
    });
    container.appendChild(approvalActions);
  }

  // Draft actions (edit, delete, submit)
  if (request.status === 'DRAFT' && isOwner) {
    // Edit button
    const editBtn = createLink({
      href: `/requests/${request.id}/edit`,
      content: `
        <span class="flex items-center gap-2">
          ${renderIcon('edit', { size: 'sm' }).outerHTML}
          <span>Edit</span>
        </span>
      `,
      className: 'inline-flex items-center gap-2 px-4 py-2 glass-button font-medium rounded-lg text-foreground',
    });
    container.appendChild(editBtn);

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
    submitBtn.innerHTML = `
      ${renderIcon('send', { size: 'sm' }).outerHTML}
      <span>Submit for Approval</span>
    `;
    submitBtn.addEventListener('click', async () => {
      const confirmed = await confirm({
        title: 'Submit for Approval',
        message: `Are you sure you want to submit "${request.title}" for approval?\n\nOnce submitted, the request will be sent for approval and cannot be edited.`,
        confirmText: 'Submit',
        cancelText: 'Cancel',
        icon: 'send',
      });

      if (!confirmed) return;

      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        ${renderIcon('loader', { size: 'sm', className: 'animate-spin' }).outerHTML}
        <span>Submitting...</span>
      `;

      try {
        await apiClient.post(`/requests/${request.id}/submit`);
        window.location.reload();
      } catch (error) {
        console.error('Failed to submit request:', error);
        submitBtn.disabled = false;
        submitBtn.innerHTML = `
          ${renderIcon('send', { size: 'sm' }).outerHTML}
          <span>Submit for Approval</span>
        `;
        await confirm({
          title: 'Submission Failed',
          message: 'Failed to submit request. Please try again.',
          confirmText: 'OK',
          icon: 'alertCircle',
        });
      }
    });
    container.appendChild(submitBtn);

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 text-destructive font-medium rounded-lg hover:bg-destructive/20 transition-colors';
    deleteBtn.innerHTML = `
      ${renderIcon('trash', { size: 'sm' }).outerHTML}
      <span>Delete</span>
    `;
    deleteBtn.addEventListener('click', async () => {
      const confirmed = await confirm({
        title: 'Delete Request',
        message: `Are you sure you want to delete "${request.title}"?\n\nThis action cannot be undone.`,
        confirmText: 'Delete',
        destructive: true,
        cancelText: 'Cancel',
        icon: 'trash',
      });

      if (!confirmed) return;

      deleteBtn.disabled = true;
      try {
        await apiClient.delete(`/requests/${request.id}`);
        window.location.href = '/dashboard';
      } catch (error) {
        console.error('Failed to delete request:', error);
        deleteBtn.disabled = false;
        await confirm({
          title: 'Delete Failed',
          message: 'Failed to delete request. Please try again.',
          confirmText: 'OK',
          icon: 'alertCircle',
        });
      }
    });
    container.appendChild(deleteBtn);
  }

  // Expense report actions for owner
  if (['DRAFT', 'PENDING', 'APPROVED'].includes(request.status) && isOwner) {
    // Check if an expense report already exists for this request
    let existingReports: { id: string }[] = [];
    try {
      existingReports = await getExpenseReportsForRequest(request.id);
    } catch {
      // If fetch fails, default to showing "Add" button
    }

    const firstReport = existingReports[0];
    if (firstReport) {
      // Show "Open Expense Report" button linking to the existing report
      const openReportBtn = document.createElement('a');
      openReportBtn.href = `/expense-reports/${firstReport.id}`;
      openReportBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
      openReportBtn.innerHTML = `
        ${renderIcon('externalLink', { size: 'sm' }).outerHTML}
        <span>Open Expense Report</span>
      `;
      openReportBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.history.pushState({}, '', `/expense-reports/${firstReport.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
      container.appendChild(openReportBtn);
    } else {
      // Show "Add Expense Report" button
      const expenseReportBtn = document.createElement('button');
      expenseReportBtn.type = 'button';
      expenseReportBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
      expenseReportBtn.innerHTML = `
        ${renderIcon('receipt', { size: 'sm' }).outerHTML}
        <span>Add Expense Report</span>
      `;
      expenseReportBtn.addEventListener('click', async () => {
        expenseReportBtn.disabled = true;
        expenseReportBtn.innerHTML = `
          ${renderIcon('loader', { size: 'sm', className: 'animate-spin' }).outerHTML}
          <span>Creating...</span>
        `;

        try {
          const response = await apiClient.post<{ data: { id: string } }>('/expense-reports', {
            expenditureRequestId: request.id,
          });
          const reportId = response.data?.data?.id;
          if (reportId) {
            window.history.pushState({}, '', `/expense-reports/${reportId}`);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
        } catch (error) {
          console.error('Failed to create expense report:', error);
          expenseReportBtn.disabled = false;
          expenseReportBtn.innerHTML = `
            ${renderIcon('receipt', { size: 'sm' }).outerHTML}
            <span>Add Expense Report</span>
          `;
          await confirm({
            title: 'Creation Failed',
            message: error instanceof Error ? error.message : 'Failed to create expense report. Please try again.',
            confirmText: 'OK',
            icon: 'alertCircle',
          });
        }
      });
      container.appendChild(expenseReportBtn);
    }
  }

  // Pending actions (cancel for owner)
  if (request.status === 'PENDING' && isOwner) {
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 text-destructive font-medium rounded-lg hover:bg-destructive/20 transition-colors';
    cancelBtn.innerHTML = `
      ${renderIcon('x', { size: 'sm' }).outerHTML}
      <span>Cancel Request</span>
    `;
    cancelBtn.addEventListener('click', async () => {
      const confirmed = await confirm({
        title: 'Cancel Request',
        message: `Are you sure you want to cancel "${request.title}"?\n\nThis will remove the request from the approval workflow.`,
        confirmText: 'Cancel Request',
        destructive: true,
        cancelText: 'Keep Request',
        icon: 'x',
      });

      if (!confirmed) return;

      cancelBtn.disabled = true;
      try {
        await apiClient.post(`/requests/${request.id}/cancel`);
        window.location.reload();
      } catch (error) {
        console.error('Failed to cancel request:', error);
        cancelBtn.disabled = false;
        await confirm({
          title: 'Cancellation Failed',
          message: 'Failed to cancel request. Please try again.',
          confirmText: 'OK',
          icon: 'alertCircle',
        });
      }
    });
    container.appendChild(cancelBtn);
  }

  // Pending actions (cancel for admin who is not the owner)
  if (request.status === 'PENDING' && isAdmin && !isOwner) {
    const adminCancelBtn = document.createElement('button');
    adminCancelBtn.type = 'button';
    adminCancelBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 text-destructive font-medium rounded-lg hover:bg-destructive/20 transition-colors';
    adminCancelBtn.innerHTML = `
      ${renderIcon('x', { size: 'sm' }).outerHTML}
      <span>Cancel Request</span>
    `;
    adminCancelBtn.addEventListener('click', async () => {
      const confirmed = await confirm({
        title: 'Cancel Request',
        message: 'Are you sure you want to cancel this request? This action cannot be undone.',
        confirmText: 'Cancel Request',
        cancelText: 'Keep Request',
        icon: 'alertTriangle',
        destructive: true,
      });
      if (!confirmed) return;

      adminCancelBtn.disabled = true;
      adminCancelBtn.innerHTML = `
        ${renderIcon('loader', { size: 'sm', className: 'animate-spin' }).outerHTML}
        <span>Cancelling...</span>
      `;

      try {
        await apiClient.post(`/requests/${request.id}/cancel`, {});
        window.location.reload();
      } catch (error) {
        console.error('Failed to cancel request:', error);
        adminCancelBtn.disabled = false;
        adminCancelBtn.innerHTML = `
          ${renderIcon('x', { size: 'sm' }).outerHTML}
          <span>Cancel Request</span>
        `;
        await confirm({
          title: 'Cancellation Failed',
          message: error instanceof Error ? error.message : 'Failed to cancel request. Please try again.',
          confirmText: 'OK',
          icon: 'alertCircle',
        });
      }
    });
    container.appendChild(adminCancelBtn);
  }

  // Back to dashboard link
  const backLink = createLink({
    href: '/dashboard',
    content: `
      <span class="flex items-center gap-2">
        ${renderIcon('chevronLeft', { size: 'sm' }).outerHTML}
        <span>Back to Dashboard</span>
      </span>
    `,
    className: 'inline-flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-foreground transition-colors',
  });
  container.appendChild(backLink);

  return container;
}

/**
 * Create the request detail view
 */
export async function createRequestDetailView(requestId: string): Promise<HTMLElement> {
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Loading state
  content.innerHTML = `
    <div class="flex items-center justify-center min-h-[400px]">
      <div class="flex flex-col items-center gap-4">
        ${renderIcon('loader', { size: 'xl', className: 'animate-spin text-primary' }).outerHTML}
        <p class="text-muted-foreground">Loading request...</p>
      </div>
    </div>
  `;

  // Load data
  try {
    const [requestResponse, stepsResponse, attachmentsResponse, commentsResponse] = await Promise.all([
      apiClient.get<{ data: ExpenditureRequest }>(`/requests/${requestId}`),
      apiClient.get<{ data: { steps: ApprovalStep[]; progress: ApprovalProgress } }>(`/requests/${requestId}/approval-steps`).catch(() => ({ data: { data: { steps: [], progress: null } } })),
      apiClient.get<{ data: Attachment[] }>(`/requests/${requestId}/attachments`).catch(() => ({ data: { data: [] } })),
      apiClient.get<{ data: Comment[] }>(`/requests/${requestId}/comments`).catch(() => ({ data: { data: [] } })),
    ]);

    const request = requestResponse.data.data;
    const approvalData = stepsResponse.data.data || { steps: [], progress: null };
    const approvalSteps = approvalData.steps || [];
    const approvalProgress = approvalData.progress || null;
    const attachments = attachmentsResponse.data.data || [];
    const comments = commentsResponse.data.data || [];

    // Creator name is now included in the request response from the backend
    // Use creator_name from response, with fallback to email or ID
    const creatorName: string | null = request.creator_name || request.creator_email || request.created_by;

    // Fetch audit log
    let auditLog: AuditLogEntry[] = [];
    try {
      const auditResponse = await apiClient.get<{ data: AuditLogEntry[] }>(`/requests/${requestId}/audit-log`);
      auditLog = auditResponse.data.data || [];
    } catch {
      // Audit log endpoint might not exist yet
    }

    // Get current user info
    let currentUserId: string | null = null;
    let userRole: string | null = null;

    const unsubscribe = subscribeToAuth((state: AuthState) => {
      const user = selectUser(state);
      if (user) {
        currentUserId = user.id;
        userRole = user.role || null;
      }
    });
    unsubscribe();

    // Build the UI
    content.innerHTML = '';

    // Request header
    const requestHeader = document.createElement('div');
    requestHeader.className = 'mb-8';

    const titleRow = document.createElement('div');
    titleRow.className = 'flex flex-col sm:flex-row sm:items-center gap-4 mb-4';

    const titleEl = document.createElement('h1');
    titleEl.className = 'text-2xl font-bold text-foreground flex-1';
    titleEl.textContent = request.title || 'Untitled Request';

    titleRow.appendChild(titleEl);
    titleRow.appendChild(createStatusBadge(request.status));
    requestHeader.appendChild(titleRow);

    // Action buttons (including approve/reject for approvers)
    requestHeader.appendChild(await createActionButtons(request, currentUserId, userRole, approvalSteps));

    content.appendChild(requestHeader);

    // Content grid
    const contentGrid = document.createElement('div');
    contentGrid.className = 'grid grid-cols-1 lg:grid-cols-3 gap-8';

    // Left column (approval stepper + audit trail)
    const leftColumn = document.createElement('div');
    leftColumn.className = 'lg:col-span-1 space-y-6';
    leftColumn.appendChild(createApprovalStepper(approvalSteps, approvalProgress));
    leftColumn.appendChild(createAuditTrailCard(auditLog));

    // Right column (request info + attachments + expense reports + comments)
    const rightColumn = document.createElement('div');
    rightColumn.className = 'lg:col-span-2 space-y-6';
    rightColumn.appendChild(createRequestInfoCard(request, creatorName));
    rightColumn.appendChild(createAttachmentsCard(attachments));

    // Add expense reports section for APPROVED requests (Department Head visibility)
    // Show if request is approved and current user is one of the approvers on the request
    // This allows Department Heads to see expense reports even if a delegate approved on their behalf
    if (request.status === 'APPROVED' && currentUserId) {
      const isApprover = approvalSteps.some(
        step => (
          step.approverId === currentUserId ||
          step.delegateId === currentUserId ||
          step.actedBy === currentUserId
        )
      );

      if (isApprover) {
        const expenseReportsCard = await createExpenseReportsCard(request, currentUserId);
        rightColumn.appendChild(expenseReportsCard);
      }
    }

    rightColumn.appendChild(createCommentsSection({
      requestId,
      initialComments: comments,
      onError: (error) => console.error('Comments error:', error),
    }));

    contentGrid.appendChild(leftColumn);
    contentGrid.appendChild(rightColumn);
    content.appendChild(contentGrid);
  } catch (error) {
    console.error('Failed to load request:', error);
    const apiError = error as { response?: { status?: number } };
    const isAccessDenied = apiError.response?.status === 403 || apiError.response?.status === 401;
    const title = isAccessDenied ? 'Access Denied' : 'Failed to load request';
    const message = isAccessDenied
      ? 'You do not have permission to view this request.'
      : 'The request could not be found or an error occurred while loading it.';
    content.innerHTML = `
      <div class="flex items-center justify-center min-h-[400px]">
        <div class="glass-card p-8 rounded-2xl text-center max-w-md">
          <div class="flex justify-center mb-4">
            <div class="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
              ${renderIcon(isAccessDenied ? 'lock' : 'alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
            </div>
          </div>
          <h2 class="text-xl font-bold text-foreground mb-2">${title}</h2>
          <p class="text-muted-foreground mb-6">${message}</p>
          <a href="/dashboard" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors">
            ${renderIcon('chevronLeft', { size: 'sm' }).outerHTML}
            <span>Back to Dashboard</span>
          </a>
        </div>
      </div>
    `;
  }

  return createSidebarLayout(content, {
    currentPath: `/requests/${requestId}`,
    title: 'Request Details',
    subtitle: 'View expenditure request details and approval status',
  });
}

/**
 * Request detail view function for router
 */
export function requestDetailView(context: RouteContext): HTMLElement | Promise<HTMLElement> {
  const requestId = context.params.id;
  if (!requestId) {
    const errorContainer = document.createElement('div');
    errorContainer.className = 'min-h-screen flex items-center justify-center p-4';
    errorContainer.innerHTML = `
      <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
        <h1 class="text-xl font-bold text-foreground mb-2">Invalid Request</h1>
        <p class="text-muted-foreground mb-4">No request ID provided.</p>
        <a href="/dashboard" class="text-primary hover:text-primary/80 underline">Back to Dashboard</a>
      </div>
    `;
    return errorContainer;
  }

  return createRequestDetailView(requestId);
}

export default requestDetailView;
